import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Check, Compass, Link2, Menu, Plus, Search, Sparkles, Users, X } from 'lucide-react';
import { COMMUNITY_CATEGORIES, type CommunityCategory, type DiscoverableSpace } from '@spawnwire/shared';
import { Button, clsx, IconButton, Input, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { CATEGORIES, CATEGORY_META } from '../../lib/discover';
import { useIsMobile } from '../../lib/hooks';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SpaceIcon } from '../shell/SpaceIcon';

function useDiscoverParams() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('category');
  const category = raw && (COMMUNITY_CATEGORIES as readonly string[]).includes(raw) ? (raw as CommunityCategory) : null;
  const q = params.get('q') ?? '';
  const set = useCallback(
    (next: { category?: CommunityCategory | null; q?: string }) => {
      const p = new URLSearchParams(params);
      if ('category' in next) {
        if (next.category) p.set('category', next.category);
        else p.delete('category');
      }
      if ('q' in next) {
        if (next.q) p.set('q', next.q);
        else p.delete('q');
      }
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  return { category, q, set };
}

/** Takes the place of the conversation list on Discover: home plus one entry per category. */
export function DiscoverSidebar() {
  const { category, set } = useDiscoverParams();
  const go = (c: CommunityCategory | null) => {
    set({ category: c });
    ui.setMobilePane('main');
  };
  const item = (active: boolean) => clsx('mb-0.5 flex h-10 w-full items-center gap-3 rounded-md px-3 text-[15px] font-medium', active ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg');
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-4 shadow-sm">
        <Compass className="size-5 text-accent" aria-hidden />
        <span className="font-semibold text-fg">Discover</span>
      </div>
      <nav aria-label="Discover categories" className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
        <button type="button" onClick={() => go(null)} aria-current={!category ? 'page' : undefined} className={item(!category)}>
          <Sparkles className="size-5" aria-hidden />
          Home
        </button>
        <p className="mb-1 mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-faint">Categories</p>
        {CATEGORIES.map((c) => {
          const { icon: Icon, label, color } = CATEGORY_META[c];
          return (
            <button key={c} type="button" onClick={() => go(c)} aria-current={category === c ? 'page' : undefined} className={item(category === c)}>
              <Icon className="size-5" style={{ color }} aria-hidden />
              {label}
            </button>
          );
        })}
        <div className="mt-4 border-t border-line pt-3">
          <button type="button" onClick={() => ui.open({ kind: 'joinSpace' })} className={item(false)}>
            <Link2 className="size-5" aria-hidden />
            Join with an invite
          </button>
          <button type="button" onClick={() => ui.open({ kind: 'createSpace' })} className={item(false)}>
            <Plus className="size-5" aria-hidden />
            Create a space
          </button>
        </div>
      </nav>
    </div>
  );
}

function CommunityCard({ space, onJoin, joining }: { space: DiscoverableSpace; onJoin: () => void; joining: boolean }) {
  const { icon: CategoryIcon, label, color } = CATEGORY_META[space.category];
  const member = useStore((s) => !!s.spaces[space.id]);
  const navigate = useNavigate();
  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-line bg-sidebar transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-pop">
      <div className="relative h-28 shrink-0" style={{ background: `linear-gradient(135deg, ${color}, ${color}66)` }}>
        {space.bannerUrl && <img src={space.bannerUrl} alt="" className="absolute inset-0 size-full object-cover" draggable={false} />}
        <div className="absolute -bottom-6 left-4 rounded-[30%] border-4 border-sidebar bg-sidebar">
          <SpaceIcon name={space.name} iconUrl={space.iconUrl} size={48} active />
        </div>
      </div>
      <div className="flex flex-1 flex-col px-4 pb-4 pt-8">
        <h3 className="truncate text-base font-bold text-fg" title={space.name}>
          {space.name}
        </h3>
        <p className="mt-1 line-clamp-3 flex-1 text-sm text-muted">{space.description || 'No description yet.'}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-success" aria-hidden />
            {space.onlineCount.toLocaleString()} online
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-faint" aria-hidden />
            {space.memberCount.toLocaleString()} {space.memberCount === 1 ? 'member' : 'members'}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium text-white" style={{ background: color }}>
            <CategoryIcon className="size-3" aria-hidden />
            {label}
          </span>
        </div>
        {member || space.joined ? (
          <Button variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/app/${space.id}`)}>
            <Check className="size-4" aria-hidden /> Joined · Open
          </Button>
        ) : (
          <Button className="mt-3 w-full" loading={joining} onClick={onJoin}>
            Join community
          </Button>
        )}
      </div>
    </article>
  );
}

/** Browse and search communities that Spawnwire staff have approved, and join them. */
export function DiscoverView() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { category, q, set } = useDiscoverParams();
  const [draft, setDraft] = useState(q);
  const [rows, setRows] = useState<DiscoverableSpace[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState<string | null>(null);
  const request = useRef(0);

  const load = useCallback(
    async (offset = 0) => {
      const id = ++request.current;
      setError(null);
      try {
        const params = new URLSearchParams({ offset: String(offset) });
        if (q) params.set('q', q);
        if (category) params.set('category', category);
        const page = await http.get<{ spaces: DiscoverableSpace[]; hasMore: boolean }>(`/discover?${params}`);
        if (id !== request.current) return;
        setRows((prev) => (offset && prev ? [...prev, ...page.spaces] : page.spaces));
        setHasMore(page.hasMore);
      } catch (err) {
        if (id === request.current) setError(errorMessage(err));
      }
    },
    [q, category],
  );

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  // Search as you type, after a short pause.
  useEffect(() => {
    if (draft.trim() === q) return;
    const t = setTimeout(() => set({ q: draft.trim() }), 300);
    return () => clearTimeout(t);
  }, [draft, q, set]);

  async function join(space: DiscoverableSpace) {
    setJoining(space.id);
    try {
      const res = await http.post<{ spaceId: string; channelId: string | null }>(`/discover/${space.id}/join`);
      ui.toast(`Welcome to ${space.name}!`, 'success');
      navigate(res.channelId ? `/app/${res.spaceId}/${res.channelId}` : `/app/${res.spaceId}`);
    } catch (err) {
      ui.toast(errorMessage(err), 'error');
      setJoining(null);
    }
  }

  const meta = category ? CATEGORY_META[category] : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {isMobile && (
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
          <IconButton icon={Menu} label="Open categories" onClick={() => ui.setMobilePane('nav')} />
          <span className="flex items-center gap-2 font-semibold text-fg">
            <Compass className="size-5 text-accent" aria-hidden /> Discover
          </span>
        </header>
      )}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
          {/* Hero with the search box. */}
          <section
            className="relative overflow-hidden rounded-2xl px-6 py-10 text-center text-white sm:py-14"
            style={{ background: meta ? `linear-gradient(135deg, ${meta.color}, #1e1f22)` : 'linear-gradient(135deg, #5865f2, #3ba55d)' }}
          >
            <div className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-white/10" aria-hidden />
            <div className="pointer-events-none absolute -bottom-16 -left-8 size-56 rounded-full bg-white/10" aria-hidden />
            <h1 className="relative text-2xl font-extrabold sm:text-3xl">{meta ? `${meta.label} communities` : 'Find your community on Spawnwire'}</h1>
            <p className="relative mx-auto mt-2 max-w-xl text-sm text-white/85 sm:text-base">
              {meta ? meta.blurb : 'From gaming to education, there is a place for you. Every community here has been checked by Spawnwire staff.'}
            </p>
            <div className="relative mx-auto mt-6 max-w-lg">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-faint" aria-hidden />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Search communities by name or topic"
                aria-label="Search communities"
                className="h-12 rounded-xl border-0 bg-white pl-10 pr-10 text-base text-black placeholder:text-gray-500"
              />
              {draft && (
                <button type="button" aria-label="Clear search" onClick={() => setDraft('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black">
                  <X className="size-5" />
                </button>
              )}
            </div>
          </section>

          {/* Category chips (the sidebar does the same on wide screens). */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" onClick={() => set({ category: null })} className={clsx('rounded-full px-3 py-1.5 text-sm font-medium', !category ? 'bg-accent text-accent-fg' : 'bg-sidebar text-muted hover:text-fg')}>
              All
            </button>
            {CATEGORIES.map((c) => {
              const { icon: Icon, label, color } = CATEGORY_META[c];
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={category === c}
                  onClick={() => set({ category: category === c ? null : c })}
                  className={clsx('inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium', category === c ? 'text-white' : 'bg-sidebar text-muted hover:text-fg')}
                  style={category === c ? { background: color } : undefined}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              );
            })}
          </div>

          <h2 className="mb-3 mt-6 text-sm font-semibold uppercase tracking-wide text-muted">
            {q ? `Results for “${q}”` : meta ? meta.label : 'Featured communities'}
          </h2>
          {error && <p className="mb-3 text-sm text-danger">{error}</p>}
          {!rows ? (
            <div className="flex justify-center py-16">
              <Spinner className="size-8" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line p-10 text-center">
              <Users className="mx-auto size-10 text-faint" aria-hidden />
              <p className="mt-3 font-semibold text-fg">{q || category ? 'No communities match' : 'No communities yet'}</p>
              <p className="mt-1 text-sm text-muted">
                {q || category ? 'Try another search or category.' : 'Be the first: create a community and submit it to Discover.'}
              </p>
              <Button className="mt-4" onClick={() => ui.open({ kind: 'createSpace' })}>
                Create a community
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {rows.map((s) => (
                <CommunityCard key={s.id} space={s} joining={joining === s.id} onJoin={() => void join(s)} />
              ))}
            </div>
          )}
          {hasMore && rows && (
            <Button variant="secondary" className="mt-4 w-full" onClick={() => void load(rows.length)}>
              Load more
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
